/** biome-ignore-all lint/correctness/useExhaustiveDependencies: apiUrlCreator changes on workspace switch */
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import {
	ProviderPanelBody,
	providerPanelStatus,
} from "@ext/articleProvider/components/ProviderPanel/ProviderPanelBody";
import { ProviderPanelSearch } from "@ext/articleProvider/components/ProviderPanel/ProviderPanelSearch";
import type { ProviderItemProps } from "@ext/articleProvider/models/types";
import t from "@ext/localization/locale/translate";
import FragmentService from "@ext/markdown/elements/fragment/edit/components/Tab/FragmentService";
import { mergeFragmentItems } from "@ext/markdown/elements/fragment/edit/logic/updateFragmentItem";
import type { FragmentItemProps, FragmentRenderData } from "@ext/markdown/elements/fragment/edit/model/types";
import { usePanelScope } from "@ext/workspaceTools/components"; // workspace-tools seam
import {
	FloatingIconButton,
	FloatingPanel,
	PanelList,
	PanelListItem,
	useFloatingPanelStore,
} from "@ui-kit/FloatingPanel";
import { ComponentVariantProvider } from "@ui-kit/Providers";
import { Tooltip, TooltipContent, TooltipTrigger } from "@ui-kit/Tooltip";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FragmentsActions } from "./FragmentsActions";
import { FRAGMENTS_PANEL_ID } from "./types/constants";
import { filterFragments, getFragmentDescription } from "./utils/fragmentsListUtils";

export const FragmentsPanel = () => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const { fragments, selectedID } = FragmentService.value;
	const fragmentsRef = useRef(fragments);
	const selectedIDRef = useRef(selectedID);
	fragmentsRef.current = fragments;
	selectedIDRef.current = selectedID;
	const [query, setQuery] = useState("");
	const [isLoading, setIsLoading] = useState(true);
	const isOpen = useFloatingPanelStore((state) => state.panels[FRAGMENTS_PANEL_ID]?.isOpen ?? false);
	const scope = usePanelScope("fragment", () => setQuery("")); // workspace-tools seam

	const refresh = useCallback(async () => {
		setIsLoading(true);
		const requestRevisions = new Map(
			Array.from(fragmentsRef.current.values()).map((fragment) => [fragment.id, fragment.revision ?? 0]),
		);
		const response = await FetchService.fetch<ProviderItemProps[]>(
			apiUrlCreator.getArticleListInGramaxDir("fragment"),
		);
		if (!response.ok) {
			setIsLoading(false);
			return [];
		}
		const fragmentItems = await response.json();
		const nextFragments = await Promise.all(
			fragmentItems.map(async (fragment): Promise<FragmentItemProps> => {
				const renderResponse = await FetchService.fetch<FragmentRenderData>(
					apiUrlCreator.getFragmentRenderData(fragment.id),
				);
				if (!renderResponse.ok) return fragment;

				const renderData = await renderResponse.json();
				return { ...fragment, description: getFragmentDescription(renderData.content) };
			}),
		);

		const mergedFragments = mergeFragmentItems(
			Array.from(fragmentsRef.current.values()),
			nextFragments,
			selectedIDRef.current,
			requestRevisions,
		);
		fragmentsRef.current = new Map(mergedFragments.map((fragment) => [fragment.id, fragment]));
		FragmentService.setItems(mergedFragments);
		setIsLoading(false);
		return mergedFragments;
	}, [apiUrlCreator]);

	useEffect(() => {
		if (!isOpen) return;
		void refresh();
	}, [isOpen, refresh]);

	const createFragment = useCallback(async () => {
		const createdFragment = await FragmentService.addNewFragment(apiUrlCreator);
		if (!createdFragment) return;

		const nextFragments = await refresh();
		const fragmentToOpen = nextFragments.find((fragment) => fragment.id === createdFragment.id);
		if (fragmentToOpen) FragmentService.openItem({ id: fragmentToOpen.id, title: fragmentToOpen.title ?? "" });
	}, [apiUrlCreator, refresh]);

	const filteredFragments = useMemo(() => filterFragments(Array.from(fragments.values()), query), [fragments, query]);

	return (
		<FloatingPanel
			headerActions={
				scope.value === "catalog" && (
					<Tooltip>
						<TooltipTrigger asChild>
							<FloatingIconButton
								data-testid="create-fragment"
								icon="plus"
								onClick={() => void createFragment()}
								size="md"
							/>
						</TooltipTrigger>
						<TooltipContent>{t("fragments-panel-create")}</TooltipContent>
					</Tooltip>
				)
			}
			icon="square-dashed-bottom"
			id={FRAGMENTS_PANEL_ID}
			title={t("fragments-panel-title")}
		>
			<ComponentVariantProvider variant="glass">
				<div className="flex min-h-0 flex-1 flex-col px-2 pb-2">
					{scope.switcher}
					{scope.content ?? (
						<>
							<ProviderPanelSearch
								onQueryChange={setQuery}
								placeholder={t("fragments-panel-search")}
								query={query}
							/>
							<ProviderPanelBody
								data-testid="fragments-empty-state"
								empty={{
									icon: "puzzle",
									title: t("fragments-panel-empty-state.title"),
									description: t("fragments-panel-empty-state.description"),
								}}
								loadingLabel={t("fragments-panel-loading")}
								notFound={t("fragments-panel-empty")}
								status={providerPanelStatus(isLoading, fragments.size, filteredFragments.length)}
							>
								<PanelList>
									{filteredFragments.map((fragment) => (
										<PanelListItem
											actions={<FragmentsActions fragment={fragment} onRefresh={refresh} />}
											data-testid="fragment-row"
											description={fragment.description || t("fragments-panel-no-content")}
											key={fragment.id}
											onOpen={() =>
												FragmentService.openItem({
													id: fragment.id,
													title: fragment.title ?? "",
												})
											}
											selected={fragment.id === selectedID}
											title={fragment.title || t("fragments-panel-untitled")}
										/>
									))}
								</PanelList>
							</ProviderPanelBody>
						</>
					)}
				</div>
			</ComponentVariantProvider>
		</FloatingPanel>
	);
};
